import { useEffect, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { usePermission } from '@/shell/module-system'
import { ArrowLeft, Download, FileText, Loader2, Save, Trash2 } from 'lucide-react'
import { Button, Input, QueryErrorState, Select, Textarea, toast } from '@/components/ui'
import {
  useCreateReport,
  useDeleteReport,
  useGeneratePdf,
  useReport,
  useUpdateReport,
} from '../api/queries'
import type {
  DisciplinaryReport,
  DisciplineAction,
  ViolationType,
} from '../api/types'
import {
  DISCIPLINE_ACTIONS_ORDERED,
  DISCIPLINE_ACTION_LABELS,
} from '../api/types'
import { DisciplinaryStatusChip } from '../components/DisciplinaryStatusChip'
import { SignOffPanel } from '../components/SignOffPanel'
import { ViolationTypePicker } from '../components/ViolationTypePicker'

// ─── Layout helpers ────────────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="bg-[#1A3A5C] text-white text-sm font-bold px-3 py-2 rounded-t">
      {label}
    </div>
  )
}

function SectionBody({ children }: { children: React.ReactNode }) {
  return (
    <div className="border border-border-default border-t-0 rounded-b p-4 space-y-4">
      {children}
    </div>
  )
}

function FieldGroup({
  children,
  cols = 3,
}: {
  children: React.ReactNode
  cols?: 2 | 3 | 4
}) {
  const cls =
    cols === 4
      ? 'grid grid-cols-1 sm:grid-cols-4 gap-4'
      : cols === 2
        ? 'grid grid-cols-1 sm:grid-cols-2 gap-4'
        : 'grid grid-cols-1 sm:grid-cols-3 gap-4'
  return <div className={cls}>{children}</div>
}

function Field({
  label,
  children,
  colSpan = 1,
}: {
  label: string
  children: React.ReactNode
  colSpan?: 1 | 2 | 3 | 4
}) {
  const spanClass =
    colSpan === 4
      ? 'sm:col-span-4'
      : colSpan === 3
        ? 'sm:col-span-3'
        : colSpan === 2
          ? 'sm:col-span-2'
          : ''
  return (
    <div className={`space-y-1 ${spanClass}`}>
      <label className="text-xs text-secondary uppercase tracking-wide font-medium">
        {label}
      </label>
      {children}
    </div>
  )
}

// ─── Form state ────────────────────────────────────────────────────

interface FormState {
  date_of_report: string
  employee_name: string
  employee_id: string
  department: string
  position_title: string
  supervisor_name: string
  date_of_incident: string
  time_of_incident: string
  location: string
  violation_types: ViolationType[]
  prior_discipline: boolean
  prior_discipline_details: string
  discipline_action: DisciplineAction | ''
  suspension_days: string
  pip_duration_days: string
  incident_description: string
  policy_violated: string
  witnesses: string
  corrective_action_required: string
  employee_statement: string
  employee_comments: string
}

function blankForm(): FormState {
  return {
    date_of_report: new Date().toISOString().slice(0, 10),
    employee_name: '',
    employee_id: '',
    department: '',
    position_title: '',
    supervisor_name: '',
    date_of_incident: '',
    time_of_incident: '',
    location: '',
    violation_types: [],
    prior_discipline: false,
    prior_discipline_details: '',
    discipline_action: '',
    suspension_days: '',
    pip_duration_days: '',
    incident_description: '',
    policy_violated: '',
    witnesses: '',
    corrective_action_required: '',
    employee_statement: '',
    employee_comments: '',
  }
}

function reportToForm(r: DisciplinaryReport): FormState {
  return {
    date_of_report: r.date_of_report ?? '',
    employee_name: r.employee_name ?? '',
    employee_id: r.employee_id ?? '',
    department: r.department ?? '',
    position_title: r.position_title ?? '',
    supervisor_name: r.supervisor_name ?? '',
    date_of_incident: r.date_of_incident ?? '',
    time_of_incident: r.time_of_incident ?? '',
    location: r.location ?? '',
    violation_types: r.violation_types ?? [],
    prior_discipline: r.prior_discipline,
    prior_discipline_details: r.prior_discipline_details ?? '',
    discipline_action: r.discipline_action ?? '',
    suspension_days: r.suspension_days != null ? String(r.suspension_days) : '',
    pip_duration_days: r.pip_duration_days != null ? String(r.pip_duration_days) : '',
    incident_description: r.incident_description ?? '',
    policy_violated: r.policy_violated ?? '',
    witnesses: r.witnesses ?? '',
    corrective_action_required: r.corrective_action_required ?? '',
    employee_statement: r.employee_statement ?? '',
    employee_comments: r.employee_comments ?? '',
  }
}

// ─── Page ──────────────────────────────────────────────────────────

export function DisciplinaryReportPage() {
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as { reportId?: string }
  const reportId = params.reportId === 'new' ? null : (params.reportId ?? null)
  const isNew = reportId === null

  const { data: report, isLoading, isError, error, refetch } = useReport(reportId)
  const createMutation = useCreateReport()
  const updateMutation = useUpdateReport(reportId ?? '')
  const deleteMutation = useDeleteReport()
  const pdfMutation = useGeneratePdf(reportId ?? '')

  const [form, setForm] = useState<FormState>(blankForm)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (report) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setForm(reportToForm(report))
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDirty(false)
    }
  }, [report])

  function set<K extends keyof FormState>(field: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }))
    setDirty(true)
  }

  const isAdmin = usePermission('admin')
  const isSaving = createMutation.isPending || updateMutation.isPending
  // Main form is read-only once issued, except for admins, who can correct
  // it at any status; ack-only fields stay editable via SignOffPanel
  const isIssued = report?.status === 'issued'
  const isAcknowledged = report?.status === 'acknowledged'
  const isReadOnly = !isNew && (isIssued || isAcknowledged) && !isAdmin
  const canDelete = !isNew && (report?.status === 'draft' || isAdmin)
  // The employee's comments stay open through the meeting (issued), and lock
  // once they acknowledge or refuse — same rule as the backend.
  const commentsEditable = !isAcknowledged || isAdmin
  const commentsChanged = (report?.employee_comments ?? '') !== form.employee_comments

  const showSuspensionDays =
    form.discipline_action === 'suspension_unpaid' ||
    form.discipline_action === 'suspension_paid'
  const showPipDays = form.discipline_action === 'performance_improvement_plan'

  function buildPayload() {
    return {
      date_of_report: form.date_of_report || new Date().toISOString().slice(0, 10),
      employee_name: form.employee_name || null,
      employee_id: form.employee_id || null,
      department: form.department || null,
      position_title: form.position_title || null,
      supervisor_name: form.supervisor_name || null,
      date_of_incident: form.date_of_incident || null,
      time_of_incident: form.time_of_incident || null,
      location: form.location || null,
      violation_types: form.violation_types,
      prior_discipline: form.prior_discipline,
      prior_discipline_details: form.prior_discipline_details || null,
      discipline_action: form.discipline_action || null,
      suspension_days: showSuspensionDays && form.suspension_days
        ? parseInt(form.suspension_days, 10)
        : null,
      pip_duration_days: showPipDays && form.pip_duration_days
        ? parseInt(form.pip_duration_days, 10)
        : null,
      incident_description: form.incident_description || null,
      policy_violated: form.policy_violated || null,
      witnesses: form.witnesses || null,
      corrective_action_required: form.corrective_action_required || null,
      employee_statement: form.employee_statement || null,
      employee_comments: form.employee_comments || null,
    }
  }

  // At "issued" the main form (and its save bar) is read-only, so the
  // comments box saves on its own.
  async function handleSaveComments() {
    try {
      await updateMutation.mutateAsync({ employee_comments: form.employee_comments || null })
      toast.success('Employee comments saved.')
      setDirty(false)
    } catch {
      toast.danger('Saving comments failed. Please try again.')
    }
  }

  async function handleSave() {
    try {
      const payload = buildPayload()
      if (isNew) {
        const created = await createMutation.mutateAsync(payload)
        toast.success(`Report ${created.report_number} created.`)
        navigate({
          to: '/disciplinary-reports/$reportId',
          params: { reportId: created.id },
        })
      } else {
        await updateMutation.mutateAsync(payload)
        toast.success('Report saved.')
        setDirty(false)
      }
    } catch {
      toast.danger('Save failed. Please try again.')
    }
  }

  async function handleGeneratePdf() {
    try {
      const result = await pdfMutation.mutateAsync()
      window.open(`/api/exports/${result.file_id}`, '_blank')
    } catch {
      toast.danger('PDF generation failed.')
    }
  }

  async function handleDelete() {
    if (!reportId) return
    if (!confirm('Delete this disciplinary report? This cannot be undone.')) return
    try {
      await deleteMutation.mutateAsync(reportId)
      toast.success('Report deleted.')
      navigate({ to: '/disciplinary-reports' })
    } catch {
      toast.danger('Delete failed.')
    }
  }

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-secondary" size={24} />
      </div>
    )
  }

  if (!isNew && isError) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <QueryErrorState
          error={error}
          onRetry={() => void refetch()}
          subject="this disciplinary report"
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Topbar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border-default bg-surface sticky top-0 z-10">
        <button
          type="button"
          onClick={() => navigate({ to: '/disciplinary-reports' })}
          className="text-secondary hover:text-primary transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold">
              {isNew
                ? 'New Disciplinary Report'
                : (report?.report_number ?? 'Disciplinary Report')}
            </h1>
            {report && <DisciplinaryStatusChip status={report.status} />}
          </div>
          {report && (
            <p className="text-xs text-secondary mt-0.5">
              Created by {report.created_by ?? 'unknown'} on{' '}
              {report.created_at ? new Date(report.created_at).toLocaleDateString() : '—'}
              {report.employee_name ? ` · ${report.employee_name}` : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {report?.pdf_file_id && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                window.open(`/api/exports/${report.pdf_file_id}`, '_blank')
              }
            >
              <Download size={14} className="mr-1" />
              PDF
            </Button>
          )}
          {!isNew && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGeneratePdf}
              loading={pdfMutation.isPending}
            >
              <FileText size={14} className="mr-1" />
              {report?.pdf_file_id ? 'Regenerate PDF' : 'Generate PDF'}
            </Button>
          )}
          {!isReadOnly && dirty && (
            <Button variant="secondary" size="sm" onClick={handleSave} loading={isSaving}>
              <Save size={14} className="mr-1" />
              Save
            </Button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={handleDelete}
              className="text-secondary hover:text-danger transition-colors ml-1"
              title="Delete report"
            >
              <Trash2 size={16} />
            </button>
          )}
          {isNew && (
            <Button variant="primary" size="sm" onClick={handleSave} loading={isSaving}>
              Create Report
            </Button>
          )}
        </div>
      </div>

      {/* Confidentiality notice */}
      <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-xs text-amber-800">
        <strong>Confidential</strong> — This document contains sensitive HR information.
        Handle according to company policy and applicable employment law.
      </div>

      {/* Form body */}
      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-6 space-y-6">

        {/* Section 1 — Employee Information */}
        <div>
          <SectionHeader label="1. EMPLOYEE INFORMATION" />
          <SectionBody>
            <FieldGroup>
              <Field label="Employee Name" colSpan={2}>
                <Input
                  value={form.employee_name}
                  onChange={(e) => set('employee_name', e.target.value)}
                  placeholder="Full legal name"
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Employee ID / Badge #">
                <Input
                  value={form.employee_id}
                  onChange={(e) => set('employee_id', e.target.value)}
                  placeholder="HR or badge ID"
                  disabled={isReadOnly}
                />
              </Field>
            </FieldGroup>
            <FieldGroup>
              <Field label="Department" colSpan={2}>
                <Input
                  value={form.department}
                  onChange={(e) => set('department', e.target.value)}
                  placeholder="e.g. Warehouse, Shipping, QC"
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Position / Title">
                <Input
                  value={form.position_title}
                  onChange={(e) => set('position_title', e.target.value)}
                  placeholder="Job title"
                  disabled={isReadOnly}
                />
              </Field>
            </FieldGroup>
            <FieldGroup cols={4}>
              <Field label="Supervisor / Manager" colSpan={3}>
                <Input
                  value={form.supervisor_name}
                  onChange={(e) => set('supervisor_name', e.target.value)}
                  placeholder="Direct supervisor name"
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Date of Report">
                <Input
                  type="date"
                  value={form.date_of_report}
                  onChange={(e) => set('date_of_report', e.target.value)}
                  disabled={isReadOnly}
                />
              </Field>
            </FieldGroup>
            <FieldGroup cols={4}>
              <Field label="Date of Incident">
                <Input
                  type="date"
                  value={form.date_of_incident}
                  onChange={(e) => set('date_of_incident', e.target.value)}
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Time of Incident">
                <Input
                  type="time"
                  value={form.time_of_incident}
                  onChange={(e) => set('time_of_incident', e.target.value)}
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Location" colSpan={2}>
                <Input
                  value={form.location}
                  onChange={(e) => set('location', e.target.value)}
                  placeholder="Where did the incident occur?"
                  disabled={isReadOnly}
                />
              </Field>
            </FieldGroup>
          </SectionBody>
        </div>

        {/* Section 2 — Violation Details */}
        <div>
          <SectionHeader label="2. VIOLATION / INFRACTION DETAILS" />
          <SectionBody>
            <div className="space-y-2">
              <label className="text-xs text-secondary uppercase tracking-wide font-medium">
                Type of Violation (check all that apply)
              </label>
              <ViolationTypePicker
                value={form.violation_types}
                onChange={(v) => set('violation_types', v)}
                disabled={isReadOnly}
              />
            </div>
            <div className="flex items-center gap-3 pt-1">
              <input
                type="checkbox"
                id="prior_discipline"
                checked={form.prior_discipline}
                onChange={(e) => set('prior_discipline', e.target.checked)}
                disabled={isReadOnly}
                className="h-4 w-4 rounded border-border-default accent-[var(--color-brand)]"
              />
              <label htmlFor="prior_discipline" className="text-sm">
                Employee has prior discipline on record
              </label>
            </div>
            {form.prior_discipline && (
              <Field label="Prior Discipline Details" colSpan={3}>
                <Textarea
                  value={form.prior_discipline_details}
                  onChange={(e) => set('prior_discipline_details', e.target.value)}
                  placeholder="Describe prior warnings, dates, and outcomes…"
                  rows={3}
                  disabled={isReadOnly}
                />
              </Field>
            )}
          </SectionBody>
        </div>

        {/* Section 3 — Discipline Action */}
        <div>
          <SectionHeader label="3. DISCIPLINE ACTION" />
          <SectionBody>
            <Field label="Action Taken">
              <Select
                value={form.discipline_action}
                onChange={(e) =>
                  set('discipline_action', e.target.value as DisciplineAction | '')
                }
                disabled={isReadOnly}
              >
                <option value="">Select action…</option>
                {DISCIPLINE_ACTIONS_ORDERED.map((action) => (
                  <option key={action} value={action}>
                    {DISCIPLINE_ACTION_LABELS[action]}
                  </option>
                ))}
              </Select>
            </Field>
            {showSuspensionDays && (
              <Field label="Suspension Duration (days)">
                <Input
                  type="number"
                  min="1"
                  value={form.suspension_days}
                  onChange={(e) => set('suspension_days', e.target.value)}
                  placeholder="Number of days"
                  disabled={isReadOnly}
                />
              </Field>
            )}
            {showPipDays && (
              <Field label="PIP Duration (days)">
                <Input
                  type="number"
                  min="1"
                  value={form.pip_duration_days}
                  onChange={(e) => set('pip_duration_days', e.target.value)}
                  placeholder="Number of days"
                  disabled={isReadOnly}
                />
              </Field>
            )}
          </SectionBody>
        </div>

        {/* Section 4 — Incident Description */}
        <div>
          <SectionHeader label="4. INCIDENT DESCRIPTION" />
          <SectionBody>
            <Field
              label="Describe What Happened (include date, time, location, and sequence of events)"
              colSpan={3}
            >
              <Textarea
                value={form.incident_description}
                onChange={(e) => set('incident_description', e.target.value)}
                placeholder="Describe the incident in detail…"
                rows={5}
                disabled={isReadOnly}
              />
            </Field>
            <Field
              label="Company Policy / Rule Violated (include policy name or reference number)"
              colSpan={3}
            >
              <Textarea
                value={form.policy_violated}
                onChange={(e) => set('policy_violated', e.target.value)}
                placeholder="e.g. Employee Handbook Section 4.2 — Attendance Policy"
                rows={2}
                disabled={isReadOnly}
              />
            </Field>
            <Field label="Witness(es)" colSpan={3}>
              <Textarea
                value={form.witnesses}
                onChange={(e) => set('witnesses', e.target.value)}
                placeholder="Names of anyone who witnessed the incident"
                rows={2}
                disabled={isReadOnly}
              />
            </Field>
          </SectionBody>
        </div>

        {/* Section 5 — Corrective Action & Response */}
        <div>
          <SectionHeader label="5. CORRECTIVE ACTION & EMPLOYEE RESPONSE" />
          <SectionBody>
            <Field label="Corrective Action Required / Expected Behavior Going Forward" colSpan={3}>
              <Textarea
                value={form.corrective_action_required}
                onChange={(e) => set('corrective_action_required', e.target.value)}
                placeholder="What must the employee do differently? What will happen if the behavior continues?"
                rows={4}
                disabled={isReadOnly}
              />
            </Field>
            <Field
              label="Employee Statement / Response (employee's version of events or rebuttal)"
              colSpan={3}
            >
              <Textarea
                value={form.employee_statement}
                onChange={(e) => set('employee_statement', e.target.value)}
                placeholder="Document the employee's response or note 'Employee declined to provide a statement'"
                rows={4}
                disabled={isReadOnly}
              />
            </Field>
          </SectionBody>
        </div>

        {/* Section 6 — Employee Comments or Concerns */}
        <div>
          <SectionHeader label="6. EMPLOYEE COMMENTS OR CONCERNS" />
          <SectionBody>
            <p className="text-xs text-secondary">
              Employee&apos;s own comments, concerns, or perspective on this report. Optional;
              leave blank if the employee has none. The printed PDF leaves lines for handwritten
              comments when this is blank.
            </p>
            <Textarea
              aria-label="Employee comments or concerns"
              value={form.employee_comments}
              onChange={(e) => set('employee_comments', e.target.value)}
              placeholder="In the employee's words: anything they want on the record about this report"
              rows={5}
              disabled={!commentsEditable}
            />
            {isIssued && isReadOnly && (
              <div className="flex justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSaveComments}
                  loading={updateMutation.isPending}
                  disabled={!commentsChanged}
                >
                  <Save size={14} className="mr-1" />
                  Save comments
                </Button>
              </div>
            )}
          </SectionBody>
        </div>

        {/* Section 7 — Sign-off */}
        {!isNew && (
          <div>
            <SectionHeader label="7. SIGN-OFF" />
            <SectionBody>
              <p className="text-xs text-secondary">
                Employee signature acknowledges receipt of this notice — not necessarily
                agreement with its contents. A copy will be provided to the employee and
                placed in their personnel file.
              </p>
              <SignOffPanel
                report={report!}
                pendingEdits={!isReadOnly && dirty ? buildPayload() : null}
              />
            </SectionBody>
          </div>
        )}

        {/* Bottom save bar */}
        {!isReadOnly && dirty && (
          <div className="flex justify-end py-4">
            <Button variant="primary" size="sm" onClick={handleSave} loading={isSaving}>
              <Save size={14} className="mr-1" />
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
